import { useEffect, useState } from 'react';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'iframe',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

const getFocusableElements = container =>
  Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)).filter(element => element.getClientRects().length > 0);

/**
 * Keeps keyboard focus inside a dialog: focuses it on open (unless content autofocused), cycles Tab/Shift+Tab within it
 * and returns focus to the previously focused element on close.
 *
 * @param containerRef {Object} - Ref to the dialog DOM node (must have tabIndex="-1").
 * @param [isOpen = true] {Boolean} - Whether the dialog is currently rendered.
 */
const useFocusTrap = (containerRef, isOpen = true) => {
  // Captured before commit: `autoFocus` inside the dialog moves focus away from the opener before effects run.
  const [focusedOnMount] = useState(() => document.activeElement);

  useEffect(() => {
    const container = containerRef.current;

    if (!isOpen || !container) return undefined;

    const isAutoFocused = container.contains(document.activeElement);
    const previouslyFocused = isAutoFocused ? focusedOnMount : document.activeElement;

    if (!isAutoFocused) {
      container.focus({ preventScroll: true });
    }

    const handleKeyDown = event => {
      if (event.key !== 'Tab' || event.defaultPrevented) return;

      const focusable = getFocusableElements(container);

      if (focusable.length === 0) {
        event.preventDefault();

        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const { activeElement } = document;

      if (event.shiftKey && (activeElement === first || activeElement === container)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    container.addEventListener('keydown', handleKeyDown);

    return () => {
      container.removeEventListener('keydown', handleKeyDown);

      if (previouslyFocused instanceof HTMLElement && document.contains(previouslyFocused)) {
        previouslyFocused.focus({ preventScroll: true });
      }
    };
  }, [containerRef, isOpen, focusedOnMount]);
};

export default useFocusTrap;
